import * as THREE from 'three';

/**
 * Postprocessing pass that pushes a photoreal scene toward a hand-drawn,
 * watercolor-on-paper feel. Five stages:
 *   1. ink edge      — luminance-gradient darken (Sobel-ish)
 *   2. posterize     — collapse colors into N steps for painted bands
 *   3. warm tint     — gentle multiply to undo the cool splat recolor
 *   4. paper grain   — procedural fiber noise
 *   5. vignette      — soft darken at frame edges
 */
export const PaperShader = {
  uniforms: {
    tDiffuse: { value: null as THREE.Texture | null },
    resolution: { value: new THREE.Vector2(1, 1) },
    posterLevels: { value: 6.0 },
    grainStrength: { value: 0.08 },
    edgeStrength: { value: 1.4 },
    vignetteStrength: { value: 0.55 },
    warmTint: { value: new THREE.Vector3(1.08, 1.0, 0.9) },
  },
  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform vec2 resolution;
    uniform float posterLevels;
    uniform float grainStrength;
    uniform float edgeStrength;
    uniform float vignetteStrength;
    uniform vec3 warmTint;
    varying vec2 vUv;

    float luma(vec3 c) { return dot(c, vec3(0.299, 0.587, 0.114)); }
    float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }

    void main() {
      vec2 px = 1.0 / resolution;
      vec3 c = texture2D(tDiffuse, vUv).rgb;

      // Ink line: 4-tap luminance gradient. Subtracts dark where the
      // image has high local contrast — reads as a hand-drawn outline.
      float lU = luma(texture2D(tDiffuse, vUv + vec2(0.0, px.y)).rgb);
      float lD = luma(texture2D(tDiffuse, vUv - vec2(0.0, px.y)).rgb);
      float lL = luma(texture2D(tDiffuse, vUv - vec2(px.x, 0.0)).rgb);
      float lR = luma(texture2D(tDiffuse, vUv + vec2(px.x, 0.0)).rgb);
      float edge = abs(lU - lD) + abs(lL - lR);
      c -= edge * edgeStrength;

      // Posterize: collapses smooth gradients into stepped bands so it
      // reads as paint on paper, not photographed continuous-tone.
      c = clamp(c, 0.0, 1.0);
      c = floor(c * posterLevels) / posterLevels;

      // Warm tint: counters the cool splat recolor so the result isn't
      // pure-blue-grey. Subtle bias toward amber.
      c *= warmTint;

      // Paper grain: hash noise quantized to 2-pixel cells so the texture
      // feels like fiber, not film.
      float grain = hash(floor(vUv * resolution * 0.5)) - 0.5;
      c += grain * grainStrength;

      // Vignette: dark soft falloff toward corners (radial gradient).
      float d = distance(vUv, vec2(0.5));
      c *= 1.0 - smoothstep(0.42, 0.95, d) * vignetteStrength;

      gl_FragColor = vec4(c, 1.0);
    }
  `,
};
